import { App as AntdApp, Card, Empty } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ContentSpinner } from 'src/components'
import { useHasWorkspacePermissions } from 'src/hooks'
import { paths } from 'src/router/paths'
import type { SquadAgentWriteInput } from 'src/services/squadAgent'
import { useShowApiErrors, WorkspacePermissionCode } from 'src/utils'

import {
  AgentForm,
  useCreateAgent,
  useRevokeAutoApprovedTool,
  useUpdateAgent,
  useWorkspaceAgent,
  useWorkspaceAgents,
  type RegisterAgentSubmit
} from '../components/agents'

import { WorkspacePageShell, useWorkspaceRoute } from './WorkspacePageShell'

// Scopes are not edited here — they live on the detail page's Access tab, gated
// by connected tools — so identity edits never touch `allowed_scopes`.
const toWriteInput = (values: RegisterAgentSubmit): SquadAgentWriteInput => ({
  name: values.name,
  description: values.description,
  system_prompt: values.systemPrompt,
  restricted: values.restricted,
  tool_execution_mode: values.toolExecutionMode,
  // Present only on create (members are pinned to personal in the form).
  ...(values.tier ? { tier: values.tier } : {}),
  // Audit metadata: the editor was warned that their own triggers on this agent
  // keep running with their permissions once it is opened up, and accepted.
  ...(values.runAsExposureAck ? { run_as_exposure_ack: true } : {})
})

export const WorkspaceAgentFormPage = () => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const showApiErrors = useShowApiErrors()
  const { id, workspaceId } = useWorkspaceRoute()
  const { agentId } = useParams<{ agentId?: string }>()
  const mode: 'create' | 'edit' = agentId ? 'edit' : 'create'
  // Agents are provisioned alongside applications, so managing them follows
  // `applications.manage`.
  const isAdmin = useHasWorkspacePermissions(
    WorkspacePermissionCode.APPLICATIONS_MANAGE
  )

  const { data: agent, isLoading: isLoadingAgent } = useWorkspaceAgent(
    workspaceId,
    agentId
  )
  const { data: allAgents = [] } = useWorkspaceAgents(workspaceId)

  const createMutation = useCreateAgent(workspaceId)
  const updateMutation = useUpdateAgent(workspaceId, agentId ?? '')
  const revokeAutoApprovedToolMutation = useRevokeAutoApprovedTool(
    workspaceId,
    agentId ?? ''
  )

  const existingNames = allAgents
    .filter((a) => a.id !== agentId)
    .map((a) => a.name)

  const goToList = () => navigate(paths.workspaces.agents(id))
  const goToDetail = (target: string) =>
    navigate(paths.workspaces.agentDetail(id, target))

  const onCancel = () =>
    mode === 'edit' && agentId ? goToDetail(agentId) : goToList()

  const onSubmit = (values: RegisterAgentSubmit) => {
    const body = toWriteInput(values)
    if (mode === 'create') {
      createMutation.mutate(body, {
        onSuccess: (created) => {
          message.success(t('workspace.agents.messages.registered'))
          goToDetail(created.id)
        },
        onError: (e) => showApiErrors(e)
      })
    } else {
      updateMutation.mutate(body, {
        onSuccess: () => {
          message.success(t('workspace.agents.messages.updated'))
          goToDetail(agentId ?? '')
        },
        onError: (e) => showApiErrors(e)
      })
    }
  }

  if (mode === 'edit' && isLoadingAgent) {
    return (
      <WorkspacePageShell breadcrumbLeaf={t('workspace.agents.form.editTitle')}>
        <ContentSpinner minHeight={240} />
      </WorkspacePageShell>
    )
  }

  if (mode === 'edit' && !agent) {
    return (
      <WorkspacePageShell
        breadcrumbLeaf={t('workspace.agents.detail.notFound')}
      >
        <Card>
          <Empty description={t('workspace.agents.detail.notFound')} />
        </Card>
      </WorkspacePageShell>
    )
  }

  return (
    <WorkspacePageShell
      sectionTitle={t(
        mode === 'create'
          ? 'workspace.agents.form.registerTitle'
          : 'workspace.agents.form.editTitle'
      )}
    >
      <div style={{ width: '100%', maxWidth: 680, alignSelf: 'center' }}>
        <AgentForm
          mode={mode}
          initial={agent}
          existingNames={existingNames}
          isAdmin={isAdmin}
          submitting={createMutation.isPending || updateMutation.isPending}
          onCancel={onCancel}
          onSubmit={onSubmit}
          onRevokeAutoApprovedTool={
            mode === 'edit' && agentId
              ? (toolName) =>
                  revokeAutoApprovedToolMutation.mutateAsync(toolName)
              : undefined
          }
        />
      </div>
    </WorkspacePageShell>
  )
}
